import { CheckCircle2, FileText, GraduationCap, Mail, Search, Send, XCircle } from 'lucide-react';
import { createElement } from 'react';
import type { LifecycleEvent } from '../../../../components/LifecycleTimeline';
import { formatDateOnly } from '../../../../lib/formatDate';
import { SURVEY_PLAN_STATUS } from '../../survey/surveyTypes';
import type { DevelopmentPlanDetail } from '../developmentPlanTypes';

/** 16px, which is what `<LifecycleTimeline>`'s rail is sized for. */
const glyph = (icon: typeof FileText) => createElement(icon, { className: 'h-4 w-4' });

/**
 * One plan's life cycle, DERIVED from columns that already exist — the assessment, the plan's
 * first write, and the approval as the engine holds it. Nothing is stored for the timeline, so
 * there is no second history to fall out of step. The same steps, in the same words, as the
 * employee's own view of it under Self Service.
 *
 * A step that has NOT happened has no instant and is not drawn — the banner says what is next.
 * `!= null` throughout: every field came off the wire, where a null is omitted.
 */
export function developmentPlanLifecycle(detail: DevelopmentPlanDetail): LifecycleEvent[] {
  const plan = detail.summary;

  // The register lists only assessed responses, so this step is always there.
  const events: LifecycleEvent[] = [
    {
      key: 'assessed',
      label: 'Assessed',
      at: plan.assessedAt,
      icon: glyph(Search),
      tone: 'blue',
      actor: plan.assessedByName,
    },
  ];

  if (plan.developmentPlanAt != null) {
    events.push({
      key: 'plan',
      label: 'Development plan written',
      at: plan.developmentPlanAt,
      icon: glyph(FileText),
      tone: 'blue',
      actor: plan.developmentPlanByName,
    });
  }

  const approval = detail.approval;

  if (approval != null) {
    events.push({ key: 'submitted', label: 'Submitted for approval', at: approval.submittedAt, icon: glyph(Send), tone: 'amber' });

    // Accepted and Declined come AFTER the approval, so the approval stays on the rail for them.
    const approved =
      plan.developmentPlanStatus === SURVEY_PLAN_STATUS.Approved ||
      plan.developmentPlanStatus === SURVEY_PLAN_STATUS.Accepted ||
      plan.developmentPlanStatus === SURVEY_PLAN_STATUS.Declined;

    if (approval.decidedAt != null && approved) {
      events.push({
        key: 'approved',
        label: 'Approved',
        at: approval.decidedAt,
        icon: glyph(CheckCircle2),
        tone: 'emerald',
        actor: approval.decidedByName,
      });
    }

    if (approval.decidedAt != null && plan.developmentPlanStatus === SURVEY_PLAN_STATUS.Rejected) {
      events.push({
        key: 'rejected',
        label: 'Rejected',
        at: approval.decidedAt,
        icon: glyph(XCircle),
        tone: 'red',
        actor: approval.decidedByName,
      });
    }
  }

  // SENT to the employee — by the approval itself (no actor) or by whoever pressed Send.
  if (plan.sentAt != null) {
    events.push({
      key: 'sent',
      label: 'Sent to the employee',
      at: plan.sentAt,
      icon: glyph(Mail),
      tone: 'blue',
      actor: plan.sentByName,
      detail: plan.sentByName == null ? 'Sent automatically on approval' : undefined,
    });
  }

  // The EMPLOYEE's answer to the approved plan, then the training they started on it.
  if (plan.employeeDecidedAt != null && plan.developmentPlanStatus === SURVEY_PLAN_STATUS.Accepted) {
    events.push({ key: 'accepted', label: 'Accepted by the employee', at: plan.employeeDecidedAt, icon: glyph(CheckCircle2), tone: 'emerald' });
  }

  if (plan.employeeDecidedAt != null && plan.developmentPlanStatus === SURVEY_PLAN_STATUS.Declined) {
    events.push({
      key: 'declined',
      label: 'Declined by the employee',
      at: plan.employeeDecidedAt,
      icon: glyph(XCircle),
      tone: 'red',
      detail: plan.declineReason ?? undefined,
    });
  }

  if (plan.trainingRecordedAt != null && plan.trainingTitle != null && plan.trainingStartDate != null && plan.trainingEndDate != null) {
    events.push({
      key: 'training',
      label: 'Training started',
      at: plan.trainingRecordedAt,
      icon: glyph(GraduationCap),
      tone: 'blue',
      detail: `${plan.trainingTitle} · ${formatDateOnly(plan.trainingStartDate)} to ${formatDateOnly(plan.trainingEndDate)}`,
    });
  }

  // COMPLETED by the employee, with what they wrote about it.
  if (plan.trainingCompletedAt != null) {
    events.push({
      key: 'training-completed',
      label: 'Training completed',
      at: plan.trainingCompletedAt,
      icon: glyph(CheckCircle2),
      tone: 'emerald',
      detail: plan.trainingCompletionRemarks ?? undefined,
    });
  }

  // The employee's REPORT on the completed training — its first submission.
  if (plan.trainingReportAt != null) {
    events.push({
      key: 'training-report',
      label: 'Report submitted',
      at: plan.trainingReportAt,
      icon: glyph(FileText),
      tone: 'blue',
    });
  }

  // The training's IMPACT, assessed by Learning & Development once the report is in.
  if (plan.impactAssessmentAt != null) {
    events.push({
      key: 'impact',
      label: 'Impact assessed',
      at: plan.impactAssessmentAt,
      icon: glyph(Search),
      tone: 'blue',
      actor: plan.impactAssessmentByName,
      detail: plan.impactAssessmentRemarks ?? undefined,
    });
  }

  return events;
}
